import { useEffect } from 'react';

const SITE_NAME = 'M.E.-Kollektiv';

export interface AlternateLink {
  hreflang: string;
  href: string;
}

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  language?: 'en' | 'de';
  /** hreflang alternates for the same page in the other languages. */
  alternates?: AlternateLink[];
}

export const useSEO = (data: SEOData) => {
  useEffect(() => {
    // Update document title
    document.title = data.title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', data.description);
    if (data.keywords) {
      updateMetaTag('keywords', data.keywords);
    }

    // Open Graph tags (for when links are shared)
    updatePropertyTag('og:title', data.title);
    updatePropertyTag('og:description', data.description);
    updatePropertyTag('og:type', data.type || 'website');
    if (data.url) {
      updatePropertyTag('og:url', data.url);
    }
    if (data.image) {
      updatePropertyTag('og:image', data.image);
    }
    updatePropertyTag('og:locale', data.language === 'de' ? 'de_DE' : 'en_US');
    updatePropertyTag('og:locale:alternate', data.language === 'de' ? 'en_US' : 'de_DE');
    updatePropertyTag('og:site_name', SITE_NAME);
    if (data.image) {
      updatePropertyTag('og:image:alt', data.title);
    }

    // Twitter Card tags (for when links are shared on Twitter).
    // These use name="..." so they update the prerendered tags in place instead
    // of adding a second set.
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', data.title);
    updateMetaTag('twitter:description', data.description);
    if (data.url) {
      updateMetaTag('twitter:url', data.url);
    }
    if (data.image) {
      updateMetaTag('twitter:image', data.image);
    }

    // Language and other meta tags
    updateMetaTag('language', data.language || 'en');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('author', SITE_NAME);

    // Update html lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = data.language || 'en';

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    if (data.url) {
      canonical.href = data.url;
    }

    // hreflang alternates - one URL per language, plus x-default pointing at
    // the unprefixed URL that negotiates the language
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
    (data.alternates || []).forEach((alternate) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alternate.hreflang;
      link.href = alternate.href;
      document.head.appendChild(link);
    });

    // Structured Data (JSON-LD) for better SEO
    let structuredData = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    
    // NGO rather than the generic Organization: it tells search engines what
    // kind of body this is, which is what the organisation wants to be found as.
    const jsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': data.type === 'article' ? 'Article' : 'WebPage',
      name: data.title,
      description: data.description,
      url: data.url || window.location.origin,
      inLanguage: data.language === 'de' ? 'de-DE' : 'en-US',
      publisher: {
        '@type': 'NGO',
        name: SITE_NAME,
        url: window.location.origin,
        areaServed: ['DE', 'AT', 'CH'],
        knowsAbout: 'Myalgic Encephalomyelitis',
      },
    };

    if (data.type === 'article') {
      jsonLd.headline = data.title;
    }

    structuredData.textContent = JSON.stringify(jsonLd);

    // Cleanup function
    return () => {
      // Titles, meta tags and the html lang attribute are not reset here: every
      // page sets its own, and resetting would briefly show hardcoded defaults
      // in the wrong language during navigation.
      const structuredData = document.querySelector('script[type="application/ld+json"]');
      if (structuredData) {
        structuredData.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.title, data.description, data.keywords, data.image, data.url, data.type, data.language, JSON.stringify(data.alternates)]);
};

